import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * useLiveMonitoring - Hook for live interview monitoring using REST polling
 * Uses polling every 3 seconds to fetch active interviews
 * This approach is more compatible with Kubernetes ingress than WebSockets
 */
const useLiveMonitoring = (userId, backendUrl) => {
  const [isConnected, setIsConnected] = useState(false);
  const [interviews, setInterviews] = useState([]);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [error, setError] = useState(null);
  const pollingRef = useRef(null);
  const isActiveRef = useRef(true);

  const fetchInterviews = useCallback(async () => {
    if (!userId || !backendUrl || !isActiveRef.current) return;

    try {
      const response = await fetch(`${backendUrl}/api/live/interviews`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setInterviews(data.interviews || []);
        setLastUpdate(new Date().toISOString());
        setIsConnected(true);
        setError(null);
      } else if (response.status === 401) {
        setIsConnected(false);
        setError('Non authentifié');
      } else {
        setError('Erreur de connexion');
      }
    } catch (err) {
      console.error('[LiveMonitoring] Fetch error:', err);
      setIsConnected(false);
      setError('Erreur réseau');
    }
  }, [userId, backendUrl]);

  const startPolling = useCallback(() => {
    isActiveRef.current = true;
    // Initial fetch
    fetchInterviews();
    
    // Start polling every 3 seconds
    pollingRef.current = setInterval(() => {
      if (isActiveRef.current) {
        fetchInterviews();
      }
    }, 3000);
    
    console.log('[LiveMonitoring] Polling started');
  }, [fetchInterviews]);

  const stopPolling = useCallback(() => {
    isActiveRef.current = false;
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    setIsConnected(false);
    console.log('[LiveMonitoring] Polling stopped');
  }, []);

  useEffect(() => {
    if (userId && backendUrl) {
      startPolling();
    }
    
    return () => {
      stopPolling();
    };
  }, [userId, backendUrl, startPolling, stopPolling]);

  return {
    isConnected,
    interviews,
    lastUpdate,
    error,
    reconnect: startPolling,
    disconnect: stopPolling
  };
};

export default useLiveMonitoring;
